import { Box, Tooltip, Typography } from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import AttachEmailOutlinedIcon from "@mui/icons-material/AttachEmailOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
  componentTokens,
  dimensions,
  typographyTokens,
} from "@accredo/fe-shared/theme/index.js";
import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";
import UsedInBadge from "./UsedInBadge.jsx";

const getFileTypeFromName = (filename) => {
  if (!filename) return "Generic";
  const lower = filename.toLowerCase().trim();
  if (lower.endsWith(".pdf")) return "PDF";
  if (
    lower.endsWith(".png") ||
    lower.endsWith(".jpg") ||
    lower.endsWith(".jpeg") ||
    lower.endsWith(".tif") ||
    lower.endsWith(".tiff")
  )
    return "Image";
  if (lower.endsWith(".msg")) return "MSG";
  if (lower.endsWith(".eml")) return "EML";
  if (lower.endsWith(".doc") || lower.endsWith(".docx")) return "DOC";
  return "Generic";
};

const getFileIcon = (filename) => {
  switch (getFileTypeFromName(filename)) {
    case "PDF":
      return <PictureAsPdfOutlinedIcon fontSize="small" />;
    case "Image":
      return <ImageOutlinedIcon fontSize="small" />;
    case "DOC":
      return <DescriptionOutlinedIcon fontSize="small" />;
    case "MSG":
      return <MailOutlineIcon fontSize="small" />;
    case "EML":
      return <AttachEmailOutlinedIcon fontSize="small" />;
    default:
      return <InsertDriveFileIcon fontSize="small" />;
  }
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  const kb = bytes / 1024;
  const mb = kb / 1024;
  if (mb >= 1) return `${mb.toFixed(1).replace(".", ",")} MB`;
  return `${Math.round(kb).toLocaleString("de-DE")} KB`;
};

/**
 * Single file row for attachment lists.
 *
 * Props:
 *   file       — { id, name, size }
 *   usedIn     — array of { source?, label } badges, or a single such object, or null
 *   onPreview  — callback; null/undefined disables the preview button
 *   onRemove   — callback; null/undefined disables the remove button
 *   disabled   — boolean: reduces row opacity (for "used in other fields" case)
 */
export default function AttachmentFileRow({
  file,
  usedIn = null,
  onPreview,
  onRemove,
  disabled = false,
}) {
  const badges = usedIn
    ? Array.isArray(usedIn)
      ? usedIn
      : [usedIn]
    : (file?.used_in?.map((label) => ({ label })) ?? null);

  return (
    <Box
      sx={{
        mt: "5px",
        paddingLeft: componentTokens.listItem.paddingLeft,
        paddingRight: componentTokens.listItem.paddingRight,
        paddingTop: componentTokens.listItem.paddingY,
        paddingBottom: componentTokens.listItem.paddingY,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: 1,
        backgroundColor: "action.hover",
        opacity: disabled ? 0.3 : 1,
        "&:hover": {
          opacity: 1,
        },
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: dimensions["2xl"],
        }}
      >
        {getFileIcon(file.name)}
        <Typography
          sx={{
            fontSize: typographyTokens.size.sm,
            color: "text.secondary",
            minWidth: "60px",
          }}
        >
          {formatFileSize(file.size)}
        </Typography>
        <Tooltip title={file.name}>
          <Box
            component="span"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              minWidth: 0,
              fontSize: typographyTokens.size.sm,
            }}
          >
            {file.name}
          </Box>
        </Tooltip>
        {badges?.map((source, idx) => (
          <UsedInBadge label={source.label} />
        ))}
      </Box>

      <Box sx={{ display: "flex", flexShrink: 0 }}>
        <DsButton
          elevation={0}
          sx={{
            height: componentTokens.listItem.actionButton.size,
            minWidth: componentTokens.listItem.actionButton.size,
            width: componentTokens.listItem.actionButton.size,
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor:
                componentTokens.listItem.actionButton.preview.hoverBackground,
              boxShadow: "none",
            },
          }}
          disabled={!onPreview}
          onClick={onPreview}
        >
          <PreviewOutlinedIcon
            sx={{
              color: componentTokens.listItem.actionButton.preview.iconColor,
            }}
          />
        </DsButton>

        <DsButton
          elevation={0}
          sx={{
            height: componentTokens.listItem.actionButton.size,
            minWidth: componentTokens.listItem.actionButton.size,
            width: componentTokens.listItem.actionButton.size,
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor:
                !disabled && onRemove
                  ? componentTokens.listItem.actionButton.remove.hoverBackground
                  : "transparent",
              boxShadow: "none",
            },
          }}
          disabled={disabled || !onRemove}
          onClick={onRemove}
        >
          <DeleteOutlinedIcon
            sx={{
              color:
                disabled || !onRemove
                  ? "text.disabled"
                  : componentTokens.listItem.actionButton.remove.iconColor,
            }}
          />
        </DsButton>
      </Box>
    </Box>
  );
}
